import { useState, useEffect, useRef, useCallback } from 'react';

const useTimer = (initialSeconds, onExpire) => {
  const [timeLeft, setTimeLeft] = useState(initialSeconds);
  const [expired, setExpired] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const intervalRef = useRef(null);
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const timeLeftRef = useRef(timeLeft);
  timeLeftRef.current = timeLeft;

  useEffect(() => {
    if (timeLeft === null || expired) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      const next = timeLeftRef.current - 1;
      timeLeftRef.current = next;
      if (next <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setExpired(true);
        setTimeLeft(0);
        if (onExpireRef.current) onExpireRef.current();
      } else {
        setTimeLeft(next);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, expired]);

  const reset = useCallback((seconds) => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    timeLeftRef.current = seconds;
    setTimeLeft(seconds);
    setExpired(false);
    setResetKey(k => k + 1);
  }, []);

  const stop = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const formatTime = useCallback((seconds) => {
    if (seconds == null) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }, []);

  return { timeLeft, expired, reset, stop, formatTime };
};

export default useTimer;
